/** ***********
*
* <user-right.service.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { DeleteMyRightsPayload, GetUsersPerFamily, GroupFamily, RequestAGroup, RoleLevelEnum, SendGroupRequest } from './../models/user-right';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SnackbarComponent } from '../components/shared/snackbar/snackbar';
import { Family, FamilyRight, ParentFamilyRight } from '../models/family';
import { User } from '../models/user';
import { DecodedToken, RightRequest, RightRequestAPIBody, RoleLevel, UpdateRightAPIPayload, Option, SpecificRoleDialogData, Group, UpdateRightActionPayload } from '../models/user-right';
import { selectProfileFamilies$, selectProfiles$ } from '../store/selectors/profile.selectors';
import { selectProfilesLoaded$ } from '../store/selectors/profile.selectors';
import { ErrorTranslationService } from './error-translation.service';
import { selectUserLoaded$ } from '../store/selectors/user-right.selectors';
import { CreateRightRequest, DeleteRightRequest, ErrorRightRequest, ErrorUpdateRight, SuccessAllUsers, SuccessManagers, SuccessUpdateRight } from '../store/actions/user-right.action';

export type GroupUpdate = Required<Pick<Group, 'id'|'name'>> & Pick<Group, 'description'|'managers'|'mobile'>;
export type GroupCreate = Omit<GroupUpdate, 'id'>;

@Injectable({
    providedIn: 'root'
})
export class UserRightService {

    private _isManager: boolean;
    private _userNgId: string;
    private _subscription: Subscription = new Subscription();

    /**
   * store pending level value in order to avoid ugly visual effect with the radiobutton
   */
    public pendingLevels: { [familyOrParentId: string]: RoleLevel } = {};

    constructor(
        private store: Store,
        private _helper: JwtHelperService,
        private _http: HttpClient,
        private _translate: TranslateService,
        private _snackBar: SnackbarComponent,
        private _errorTranslationService: ErrorTranslationService,
    ) {
    }

    //#region GETTERS

    /**
     * Return the current user NG ID
     */
    public getUserNgId() {
        if (!this._userNgId) {
            const decodedToken: DecodedToken = this._helper.decodeToken(localStorage.getItem('token'));
            this._userNgId = decodedToken.preferred_username;
        }
        return this._userNgId;
    }

    /**
     * Check user are already loaded or not in store(if not call API call)
     */
    public checkUserLoaded() {
        this._subscription.add(this.store.select(selectUserLoaded$).pipe(
            take(1),
            filter(loaded => !loaded),
            switchMap(() => this.getIsManager$()),
            filter(isManager => isManager),
            switchMap(() => this.getAllUsers$())
        ).subscribe(allUsers => this.store.dispatch(new SuccessAllUsers(allUsers))));
    }


    /**
     * Return wether the current user is manager (or administrator) for at least one family or parent family
     * - Check parent families right
     * - If the user is not manager or admin for a parent family right, check families right
     */
    public getIsManager$(): Observable<boolean> {
        const obs = new Observable<boolean>((observer) => {
            // if this._isManager is already defined, resolve the observable
            if (this._isManager !== undefined) {
                observer.next(this._isManager);
                observer.complete();
                return;
            }

            // make sure that the data is loaded
            const loadSub = new Subscription();
            loadSub.add(this.store.select(selectProfilesLoaded$).subscribe(isLoaded => {
                if (isLoaded) {
                    // the data is loaded, unsubscribe as it is no longer needed
                    loadSub.unsubscribe();
                    this.store.select(selectProfiles$).pipe(take(1)).subscribe(({ profiles }) => {
                        // Check if the user is administrator
                        if (profiles.userRights.isAdmin) {
                            this._isManager = true;
                            observer.next(this._isManager);
                            observer.complete();
                            return;
                        }
                        // check if the user is manager for at least one parent family
                        for (const parent of profiles.parent_families) {
                            if (parent.level === 'manager') {
                                this._isManager = true;
                                observer.next(this._isManager);
                                observer.complete();
                                return;
                            }
                        }

                        // if the user is not manager for a parent family, check for asset families
                        if (this._isManager === undefined) {
                            this.store.select(selectProfileFamilies$).pipe(take(1)).subscribe((families) => {
                                for (const family of families) {
                                    if (family.level === 'manager') {
                                        this._isManager = true;
                                        break;
                                    }
                                }
                                // set the this._isManager to false if the variable is still undefined
                                this._isManager = this._isManager ? true : false;
                                observer.next(this._isManager);
                                observer.complete();
                            });
                        } else {
                            this._isManager = this._isManager ? true : false;
                            observer.next(this._isManager);
                            observer.complete();
                        }
                    });
                }
            }));

        });

        return obs;
    }

    /**
     * Get all registered users
     */
    public getAllUsers$(): Observable<User[]> {
        return this._http.get(`${environment.API_ENDPOINT}/users`).pipe(
            catchError((err) => {
                this._errorTranslationService.handleError(err, 'BANNER_FAIL_GET_ALL_USERS');
                return of([]);
            }),
            map((allUsers: User[]) => {
                return allUsers;
            })
        );
    }


    /**
     * Call the API in order to fetch the user parent families rigth
     * @param userId The user ID for who we want to fetch the parent families rights
     */
    public getUserParentFamilyRights(userId: string): Observable<ParentFamilyRight[]> {
        // URL to be updated according to the back end
        return this._http.get(`${environment.API_ENDPOINT}/parent-families-rights/users/${userId}`).pipe(map(
            (userParentFamiliesRight: ParentFamilyRight[]) => {
                userParentFamiliesRight = userParentFamiliesRight.map(
                    (parentFamily) => {
                        return Object.assign(parentFamily, { userId: userId });
                    }
                );

                return userParentFamiliesRight;
            })
        );
    }

    /**
     * Get manager list for parent or asset family
     * - Route to the corresponding method (family or parent family)
     * - Handle redux
     * @param parentFamily Parent Family ID
     * @param familyId Family ID - optional
     */
    public getManagers(parentFamilyId: string, familyId?: string): Observable<User[]> {
        const famOrParentId = familyId ? familyId : parentFamilyId;

        let getManagers$: Observable<User[]>;
        if (familyId) {
            getManagers$ = this.getManagersByFamilyId(familyId);
        } else {
            getManagers$ = this.getManagersByParentFamilyId(parentFamilyId);
        }
        return getManagers$.pipe(take(1), tap((managers) => {
            const managerActionPayload = {
                managers: managers,
                familyId: famOrParentId
            };
            this.store.dispatch(new SuccessManagers(managerActionPayload));
        }));
    }

    /**
     * Return users who have the manager role on the specified family
     * @param familyId The family ID
     */
    public getManagersByFamilyId(familyId: string): Observable<User[]> {
        return this._http.get(`${environment.API_ENDPOINT}/families/${familyId}/manager`).pipe(
            map((managers: User[]) => {
                return managers;
            })
        );
    }

    /**
     * Return users who have the manager role on the specified parent family
     * @param parentFamilyId The parent family ID
     */
    public getManagersByParentFamilyId(parentFamilyId: string): Observable<User[]> {
        return this._http.get(`${environment.API_ENDPOINT}/parent-families/${parentFamilyId}/manager`).pipe(
            map((managers: User[]) => {
                return managers;
            })
        );
    }

    public getUsersPerFamily$(familyId: ParentFamilyRight['id']|FamilyRight['id']): Observable<GetUsersPerFamily[]> {
      return this._http.get<GetUsersPerFamily[]>(`${environment.API_ENDPOINT}/families-rights/families/${familyId}`).pipe(
        catchError((error) => {
          this._errorTranslationService.handleError(error, 'BANNER_FAIL_GET_USERS_PER_FAMILY');
          return of([]);
        })
      );
    }

    /**
     * Return all user groups
     */
    public getUserGroups$(): Observable<Group[]> {
      return this._http.get<Group[]>(`${environment.API_ENDPOINT}/groups`);
    }

    /**
     * Return the users assigned to a group
     * @param group The group
     */
    public getUsersOfGroup$(group: Group): Observable<User[]> {
      return this._http.get<User[]>(`${environment.API_ENDPOINT}/groups/${group.id}/users`);
    }

    /**
     * Return the families assigned to a group
     * @param group The group
     */
    public getFamiliesOfGroup$(group: Group): Observable<GroupFamily[]> {
      return this._http.get<GroupFamily[]>(`${environment.API_ENDPOINT}/groups/${group.id}/families`);
    }

    /**
     * Returns the list of groups of the user
     * @param userId The user id
     */
    public getGroupOfUser$(userId: string): Observable<Group> {
      return this._http.get<Group>(`${environment.API_ENDPOINT}/users/${userId}/groups`);
    }

    /**
     * Returns All groups Data(consist only name and Id)
     */
    public getAllGroups$(): Observable<Group[]> {
        return this._http.get<Group[]>(`${environment.API_ENDPOINT}/groups-list/request`);
    }

    //#endregion

    //#region  USER ROLES MANAGEMENT

    /**
     * Define the request context (family or parent family) and routes to the corresponding method in order to change the user
     * role for the given family or parent family
     */
    public handleUserRoleChange(userId: string, parentFamily: ParentFamilyRight, level: RoleLevel, family?: FamilyRight): Observable<UpdateRightAPIPayload[]> {
        // identify the context of the request (family or parent family)
        const requestContext = family ? family : parentFamily;
        // identify if the request is for creation or update
        const isRoleCreation = requestContext.level === RoleLevelEnum.basic ? true : false;
        // identify if the request is for family or parent
        const isFamily = family ? true : false;
        // call the API depending of the context
        if (isRoleCreation && isFamily) {
            return this.createFamilyUserRole([userId], family, parentFamily, level);
        } else if (!isRoleCreation && isFamily) {
            return this.updateFamilyUserRole([userId], family, parentFamily, level);
        } else if (isRoleCreation && !isFamily) {
            return this.createParentFamilyUserRole(userId, parentFamily, level);
        } else if (!isRoleCreation && !isFamily) {
            return this.updateParentFamilyUserRole(userId, parentFamily, level);
        }
    }

    /**
     * Call the API in order to set a family role for a batch of users who has no right on the given asset family
     * @param users The users' IDs hash
     * @param familyId The asset family ID
     * @param parentFamilyId The asset parent family ID
     * @param level The role level to set
     */
    public createFamilyUserRole(users: string[], family: FamilyRight, parentFamily: ParentFamilyRight, level: RoleLevel) {
        const body = {
            level: level,
            users,
        };
        return this._http.post(`${environment.API_ENDPOINT}/rights/users/families/${family.id}`, body)
            .pipe(
                catchError((err) => {
                    this._handleChangeRoleError(err);
                    return of(null);
                }),
                map((res: UpdateRightAPIPayload[]) => {
                    if (res === null) {
                        return;
                    }
                    this._handleChangeRoleSuccess(res, users, parentFamily);
                    return res;
                })
            );
    }

    /**
     * Call the API in order to change a family role for a batch of users
     * @param users The users' IDs hash
     * @param parentFamilyId The family ID
     * @param level The role level to set
     */
    public updateFamilyUserRole(users: string[], family: FamilyRight, parentFamily: ParentFamilyRight, level: RoleLevel) {
        const body = {
            level: level,
            users,
        };
        return this._http.put(`${environment.API_ENDPOINT}/rights/users/families/${family.id}`, body)
            .pipe(
                catchError((err) => {
                    this._handleUpdateChangeRoleError(err, users, parentFamily, family);
                    return of(null);
                }),
                map((res: UpdateRightAPIPayload[]) => {
                    if (res === null) {
                        return;
                    }
                    this._handleChangeRoleSuccess(res, users, parentFamily);
                    return res;
                })
            );
    }

    /**
     * Call the API in order to set a parent family role for a given user who has no right on the given parent Family
     * @param userId The user ID hash
     * @param parentFamilyId The parent family ID
     * @param level The role level to set
     */
    public createParentFamilyUserRole(userId: string, parentFamily: ParentFamilyRight, level: RoleLevel) {
        const body = {
            level: level
        };
        return this._http.post(`${environment.API_ENDPOINT}/rights/users/${userId}/parent-families/${parentFamily.id}`, body)
            .pipe(
                catchError((err) => {
                    this._handleChangeRoleError(err);
                    return of(null);
                }),
                map((res: UpdateRightAPIPayload[]) => {
                    if (res === null) {
                        return;
                    } else {
                        this._handleChangeRoleSuccess(res, [userId], parentFamily);
                        return res;
                    }
                })
            );
    }

    /**
     * Call the API in order to change a parent family role for a given user
     * @param userId The user ID hash
     * @param parentFamilyId The parent family ID
     * @param level The role level to set
     */
    public updateParentFamilyUserRole(userId: string, parentFamily: ParentFamilyRight, level: RoleLevel) {
        const body = {
            level: level
        };
        return this._http.put(`${environment.API_ENDPOINT}/rights/users/${userId}/parent-families/${parentFamily.id}`, body)
            .pipe(
                catchError((err) => {
                    this._handleUpdateChangeRoleError(err, [userId], parentFamily);
                    return of(null);
                }),
                map((res: UpdateRightAPIPayload[] | null) => {
                    if (res === null) {
                        return;
                    } else {
                        this._handleChangeRoleSuccess(res, [userId], parentFamily);
                        return res;
                    }
                })
            );
    }

    /**
    * Called to remove a role for a given user, family or/and parent
    * - Set the 'basic' role
    * @param userId The user for who we want to modify the role level
    * @param parentFamilyId The ID of the parent family impacted
    * @param familyId  The ID of the family impacted
    */
    public removeUserRole(userId: string, parentFamily: ParentFamilyRight, family?: FamilyRight) {
        if (family && family.id) {
            return this.deleteFamilyUserRole([userId], family, parentFamily);
        } else {
            return this.deleteParentFamilyUserRole(userId, parentFamily);
        }
    }

    /**
     * Call the API in order to delete a role for batch of users and asset family
     * @param users The users' IDs hash
     * @param familyId The family ID
     */
    public deleteFamilyUserRole(users: string[], family: FamilyRight, parentFamily: ParentFamilyRight) {
      const body = { users };
      return this._http.delete(`${environment.API_ENDPOINT}/rights/users/families/${family.id}`, { body })
            .pipe(
                catchError((err) => {
                    this._handleUpdateChangeRoleError(err, users, parentFamily, family);
                    return of(false);
                }),
                map((res: boolean) => {
                    if (res !== false) {
                      users.forEach(userId => {
                        const actionPayload: UpdateRightActionPayload[] = [{
                            userId: userId,
                            familyId: family.id,
                            familyLevel: 'basic',
                            parentFamilyId: parentFamily.id,
                            expanded: parentFamily.expanded
                        }];
                        this.store.dispatch(new SuccessUpdateRight(actionPayload));
                      });
                      const text = this._translate.instant('MANAGE_RIGHT_ROLE_UPDATED');
                      this._snackBar.open(text, 'green-snackbar', 5000);
                    } else {
                        this._handleChangeRoleError(res);
                    }
                })
            );
    }

    /**
     * Call the API in order to delete a role for a user and a parent family
     * @param userId The user ID hash
     * @param parentFamilyId The parent family ID
     */
    public deleteParentFamilyUserRole(userId: string, parentFamily: ParentFamilyRight) {
        return this._http.delete(`${environment.API_ENDPOINT}/rights/users/${userId}/parent-families/${parentFamily.id}`)
            .pipe(
                catchError((err) => {
                    this._handleUpdateChangeRoleError(err, [userId], parentFamily);
                    return of(false);
                }),
                map((res: boolean) => {
                    if (res !== false) {
                        const actionPayload: UpdateRightActionPayload[] = [{
                            userId: userId,
                            parentFamilyId: parentFamily.id,
                            parentFamilyLevel: 'basic',
                            expanded: parentFamily.expanded
                        }];
                        this.store.dispatch(new SuccessUpdateRight(actionPayload));

                        const text = this._translate.instant('MANAGE_RIGHT_ROLE_UPDATED');
                        this._snackBar.open(text, 'green-snackbar', 5000);
                    }
                    return;
                })
            );
    }

    /**
     * Handle the role change after a success. Dispatch the new data
     * @param res The serveur response returned after a role modification
     * @param userId The user ID hash
     * @param parentFamilyId The parent family ID
     * @param familyId The family ID. Undefined if the role modification is for a parent family
     */
    private _handleChangeRoleSuccess(res: UpdateRightAPIPayload[], users: string[], parentFamily: ParentFamilyRight) {

      users.forEach(
        userId => {
          const updateActionPayload: UpdateRightActionPayload[] = res.map((item) => {
              return {
                  userId: userId,
                  parentFamilyId: item.parentFamilyId,
                  parentFamilyLevel: item.level,
                  familyId: item.familyId,
                  familyLevel: item.familyId ? item.level : undefined,
                  expanded: parentFamily.expanded
              };
          });
          this.store.dispatch(new SuccessUpdateRight(updateActionPayload));
        }
      );
      const text = this._translate.instant('MANAGE_RIGHT_ROLE_UPDATED');
      this._snackBar.open(text, 'green-snackbar', 5000);
    }

    /**
     * Error handeling
     * Display error snack bar
     */
    private _handleChangeRoleError(error) {
        this._errorTranslationService.handleError(error, 'BANNER_FAIL_EDIT');
        this.store.dispatch(new ErrorUpdateRight());
        return null;
    }

    private _handleUpdateChangeRoleError(error, users: string[], parentFamily: ParentFamilyRight, family?: FamilyRight) {
        this._errorTranslationService.handleError(error, 'BANNER_FAIL_EDIT');
        this.store.dispatch(new ErrorUpdateRight());
        throw { parentFamily, family, users };
    }

    //#endregion
    //#region ROLE REQUEST MANAGEMENT

    /**
     * Retrieve the pending requests assigned to the current user
     */
    public getPendingRequests(): Observable<RightRequest[]> {
        return this._http.get(`${environment.API_ENDPOINT}/right-request`).pipe(
            catchError((err) => {
                this._errorTranslationService.handleError(err, 'REQUEST_RIGHT_ERROR_REQUEST_RETRIEVAL');
                return of([]);
            }),
            map((requests: RightRequest[]) => {
                return requests;
            })
        );
    }

    /**
     * Create a new right request
     */
    public requestRight(managerId: string, level: RoleLevel, parentFamilyId: string, scope: string, familyId?: string): Observable<RightRequest> {
        const body: RightRequestAPIBody = {
            managerId: managerId,
            level: level,
            parentFamilyId: parentFamilyId,
            familyId: familyId,
            scope: scope
        };
        return this._http.post(`${environment.API_ENDPOINT}/right-request`, body).pipe(
            catchError((err) => {
                this._handleRightRequestError(err);
                return of(false);
            }),
            map((rightRequest: RightRequest | false) => {
                if (rightRequest !== false) {
                    const text = this._translate.instant('REQUEST_RIGHT_STATUS_TEXT', { level: rightRequest.level, manager: rightRequest.managerMail });
                    this._snackBar.open(text);
                    this.store.dispatch(new CreateRightRequest(rightRequest));
                    return rightRequest;
                }
            })
        );
    }

    /**
     * Cancel a request
     * @param requestId The request ID
     */
    public cancelRequest(requestId: string): Observable<void> {
        return this._http.delete(`${environment.API_ENDPOINT}/right-request/${requestId}`).pipe(
            catchError((err) => {
                this._handleRightRequestError(err);
                return of(false);
            }),
            map((res) => {
                /* the API respons is null if the deletion worked */
                if (res !== false) {
                    this._snackBar.open(this._translate.instant('REQUEST_RIGHT_CANCELED'));
                    this.store.dispatch(new DeleteRightRequest(requestId));
                }
                return;
            })
        );
    }

    /**
     * Accept the specified role request
     */
    public acceptDemand(request: RightRequest): Observable<void> {
        const url = request?.groupId ? 'validate-group-request' : 'validate-right-request';
        const showMessage = request?.groupId ? 'MANAGE_GROUP_SUCCESS_ACCEPT' : 'MANAGE_RIGHT_SUCCESS_ACCEPT';
        return this._http.post(`${environment.API_ENDPOINT}/${url}/${request.id}`, {}).pipe(
            catchError((err) => {
                this._handleRightRequestError(err);
                return of(false);
            }),
            map((res: boolean) => {
                if (res !== false) {
                    this.store.dispatch(new DeleteRightRequest(request.id));
                    const text = this._translate.instant(showMessage);
                    this._snackBar.open(text, 'green-snackbar', 5000);
                }
                return;
            })
        );
    }


    /**
     * Refuse the specified role request
     */
    public refuseDemand(request: RightRequest) {
        const url = request?.groupId ? 'decline-group-request' : 'decline-right-request';
        const showMessage = request?.groupId ? 'MANAGE_GROUP_SUCCESS_REFUSE' : 'MANAGE_RIGHT_SUCCESS_REFUSE';
        return this._http.post(`${environment.API_ENDPOINT}/${url}/${request.id}`, {}).pipe(
            catchError((err) => {
                this._handleRightRequestError(err);
                return of(false);
            }),
            map((res: boolean) => {
                if (res !== false) {
                    this.store.dispatch(new DeleteRightRequest(request.id));
                    const text = this._translate.instant(showMessage);
                    this._snackBar.open(text, 'green-snackbar', 5000);
                }
                return;
            })
        );
    }

    /**
     * Update specific role of user
     */
    public updateSpecificRole(payload: {
        option: Option;
        userId: string;
    }): Observable<SpecificRoleDialogData> {
        return this._http.put<SpecificRoleDialogData>(`${environment.API_ENDPOINT}/users/${payload.userId}`, payload.option);
    }

    /**
     * Handle error for right requests management
     */
    private _handleRightRequestError(err?) {
        this._errorTranslationService.handleError(err, 'REQUEST_RIGHT_ERROR_MANAGEMENT');
        this.store.dispatch(new ErrorRightRequest());
    }

    //#endregion
    // #region GROUP MANAGEMENT

    /**
     * Update a group
     * @param group The group to update
     * @param users The users of this group
     */
    public updateGroup$(group: GroupUpdate, users?: User['id'][], families?: GroupFamily[]): Observable<Group> {
      const payload = {
        name: group.name,
        description: group.description,
        managers: group.managers,
        mobile: group.mobile,
        users,
        families
      };
      return this._http.put<Group>(`${environment.API_ENDPOINT}/groups/${group.id}`, payload);
    }

    public transferGroupOwnership$(group: Group, transferTo: User['id']): Observable<Group> {
        return this._http.put<Group>(`${environment.API_ENDPOINT}/groups/${group.id}/transfer`, {
            user: transferTo,
        });
    }

    /**
     * Create a new group
     * @param group The group to create
     * @param users The users of the group
     */
    public createGroup$({ name, description, managers, mobile }: GroupCreate, users?: User['id'][], families?: GroupFamily[]): Observable<Group> {
      const payload = { name, description, managers, mobile, users, families };
      return this._http.post<Group>(`${environment.API_ENDPOINT}/groups`, payload);
    }

    /**
     * Delete a group
     * @param group The group to delete
     */
    public deleteGroup$({ id }: Group): Observable<string> {
      return this._http.delete<void>(`${environment.API_ENDPOINT}/groups/${id}`).pipe(
        map(() => id),
      );
    }

    /**
     * Add users to a group
     * @returns The group with the updated counter of users
     */
    public addUsersToGroup$(group: Group, users: User['id'][]): Observable<User[]> {
      return this._http.post<User[]>(`${environment.API_ENDPOINT}/groups/${group.id}/users`, { users });
    }

    /**
     * Remove users from a group
     * @returns The group with the updated counter of users
     */
    public removeUsersFromGroup$(group: Group, users: User['id'][]): Observable<User[]> {
      return this._http.delete<User[]>(`${environment.API_ENDPOINT}/groups/${group.id}/users`, {
        body: { users }
      });
    }

    /**
     * Add families to a group
     * @returns The families related to this group
     */
    public addFamiliesToGroup$(group: Group, families: GroupFamily[]): Observable<GroupFamily[]> {
      return this._http.post<GroupFamily[]>(
        `${environment.API_ENDPOINT}/groups/${group.id}/families`,
        families.map(
          ({ id, level, scope }) => ({ id, level, scope })
        ),
      );
    }

    /**
     * Change the level of the families of a group
     * @returns The families related to this group
     */
    public changeGroupFamiliesLevel$(group: Group, families: GroupFamily[]): Observable<GroupFamily[]> {
      return this._http.put<GroupFamily[]>(
        `${environment.API_ENDPOINT}/groups/${group.id}/families`,
        families.map(
          ({ id, level }) => ({ id, level })
        )
      );
    }

    /**
     * Remove families from a group
     * @returns The families related to this group
     */
    public removeFamiliesFromGroup$(group: Group, families: Family['id'][]): Observable<GroupFamily[]> {
      return this._http.delete<GroupFamily[]>(`${environment.API_ENDPOINT}/groups/${group.id}/families`, {
        body: families,
      });
    }
    /**
     * Remove families from a group
     * @returns The families related to this group
     */
    public deleteMyRights$(rightToDelete: DeleteMyRightsPayload): Observable<DeleteMyRightsPayload> {
        return this._http.delete<DeleteMyRightsPayload>(`${environment.API_ENDPOINT}/delete-my-right`, {
            body: rightToDelete,
        });
      }

    public raiseGroupRequest$(payload: SendGroupRequest): Observable<RequestAGroup> {
        return this._http.post<RequestAGroup>(`${environment.API_ENDPOINT}/groups/access-request`, 
            payload
        );
    } 

    /**
     * Returns the list of groups of the user
     * @param userId The user id
     */
    public checkRequestExist$(userId: string): Observable<RequestAGroup> {
        return this._http.get<RequestAGroup>(`${environment.API_ENDPOINT}/groups/check-request/${userId}`);
    }

    /**
   * Returns the list of groups of the user
   * @param userId The user id
   */
    public cancelGroupRequest$(groupData: Group): Observable<RequestAGroup> {
        return this._http.delete<RequestAGroup>(`${environment.API_ENDPOINT}/groups/delete-request/${groupData.id}`, {
            body: { 'groupName': groupData.name }
        });
    }

    //#endregion
}
